import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ProductTourState, PersistedTourState } from '../types'

const initialState: ProductTourState = {
  status: 'idle',
  currentStep: 0,
  hasShown: false,
}

const isValidPersistedState = (v: unknown): v is PersistedTourState => {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return (
    typeof o.status === 'string' &&
    ['idle', 'running', 'paused', 'completed', 'skipped'].includes(o.status as string) &&
    typeof o.currentStep === 'number' &&
    typeof o.hasShown === 'boolean'
  )
}

const slice = createSlice({
  name: 'productTour',
  initialState,
  reducers: {
    tourStarted: (state) => {
      state.status = 'running'
      state.currentStep = 1
      state.hasShown = true
    },
    tourSkipped: (state) => {
      state.status = 'skipped'
      state.hasShown = true
    },
    stepAdvanced: (state) => {
      state.currentStep += 1
    },
    stepSkippedAhead: (state, action: PayloadAction<number | undefined>) => {
      // Advance multiple steps in a single dispatch — used by the Skip
      // tertiary button on steps that want to bypass the immediate next
      // step (e.g. job-create-submit skipping job-options-close).
      const delta = action.payload ?? 2
      state.currentStep += delta
      if (state.status !== 'running') state.status = 'running'
    },
    stepReverted: (state) => {
      if (state.currentStep > 1) state.currentStep -= 1
    },
    tourPaused: (state) => {
      if (state.status === 'running') state.status = 'paused'
    },
    tourResumed: (state) => {
      // Allow `skipped -> running` too so the header TourLauncher can pick
      // the tour back up from where the user bailed without resetting the
      // step counter (which restart() would do).
      if (state.status === 'paused' || state.status === 'skipped') {
        state.status = 'running'
      }
    },
    tourCompleted: (state) => {
      state.status = 'completed'
    },
    tourRestarted: () => initialState,
    tourHydrated: (state, action: PayloadAction<unknown>) => {
      if (isValidPersistedState(action.payload)) {
        state.status = action.payload.status
        state.currentStep = action.payload.currentStep
        state.hasShown = action.payload.hasShown
      }
    },
  },
})

export const {
  tourStarted,
  tourSkipped,
  stepAdvanced,
  stepSkippedAhead,
  stepReverted,
  tourPaused,
  tourResumed,
  tourCompleted,
  tourRestarted,
  tourHydrated,
} = slice.actions

export default slice.reducer
